import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useAppActions, useAppState } from '../../../overmind';
import { FetchType } from '../../../enums';
import { PagePath } from '../../../navigation/navigation.enums';
import { useForm } from 'react-hook-form';
import { Button, FieldController, PageHeader, RequiredValidator, TextField } from '@keplerco/core';
import { ContextDto } from '../../../overmind/actions/ai/ai-contexts';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';

export function WebSearchContextPage(): JSX.Element {
  const { contextId } = useParams();
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const { control, handleSubmit, reset } = useForm();

  const [context, setContext] = useState<ContextDto>();
  const [searchResults, setSearchResults] = useState<string>();

  async function getContext() {
    if (contextId === 'websearch' || !contextId) return;

    actions.startLoader({ path: PagePath.aiManagementContext, type: FetchType.PageFetching });

    const response = await actions.getContextById(contextId);
    setContext(response);
    setSearchResults(response?.text);
    reset({
      label: response?.label,
      searchTerm: '',
    });

    actions.stopLoader(PagePath.aiManagementContext);
  }

  useEffect(() => {
    getContext();
  }, [contextId]);

  return (
    <PageLoaderLayer path={PagePath.aiManagementContext}>
      <div className="wrapper stack">
        <PageHeader
          breadcrumbs={[
            { name: 'AI management', url: `${PagePath.aiManagementBase}${PagePath.aiManagementAgents.replace(':companySlug', companyVariables.slug!)}` },
            { name: 'Contexts', url: `${PagePath.aiManagementBase}${PagePath.aiManagementContexts.replace(':companySlug', companyVariables.slug!)}` },
            { name: contextId === 'websearch' ? 'Create web search context' : `Edit ${context?.label ?? 'context'}`, url: location.pathname },
          ]}
          title={contextId === 'websearch' ? 'Create web search context' : `Edit ${context?.label ?? 'context'}`}
          subtitle="Create or edit a web search context for your AI agents"
          divider
        />

        <form
          id="websearchContext"
          style={{ marginTop: 24, width: '100%' }}
          onSubmit={handleSubmit(async fieldValues => {
            if (!companyVariables.slug) return;

            actions.startLoader({ path: PagePath.aiManagementContext, type: FetchType.Sending });

            const nextContextId = await actions.upsertWebSearchContext({
              id: contextId === 'websearch' ? undefined : contextId,
              label: fieldValues.label,
              searchTerm: fieldValues.searchTerm,
            });

            if (!nextContextId) {
              actions.stopLoader(PagePath.aiManagementContext);
              return;
            }

            if (contextId === 'websearch') {
              keplerNavigate(`${PagePath.aiManagementBase}${PagePath.aiManagementContext.replace(':companySlug', companyVariables.slug).replace(':contextId', nextContextId)}`);
            } else {
              await getContext();
            }

            actions.stopLoader(PagePath.aiManagementContext);
          })}
        >
          <FieldController
            control={control}
            name="label"
            rules={new RequiredValidator('Enter a label')}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="text"
                label="Label"
                responsive
                validation={{
                  dirty: fieldState.isDirty || !!fieldState.error,
                  invalid: fieldState.invalid || !!fieldState.error,
                  message: fieldState.error?.message ?? 'Enter a label',
                }}
              />
            )}
          />

          <FieldController
            control={control}
            name="searchTerm"
            rules={new RequiredValidator(contextId === 'websearch' ? 'Enter a search term' : false)}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="text"
                label="Search term"
                responsive
                validation={{
                  dirty: fieldState.isDirty || !!fieldState.error,
                  invalid: fieldState.invalid || !!fieldState.error,
                  message: fieldState.error?.message ?? 'Enter a search term',
                }}
              />
            )}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="button">Save</Button>
          </div>

          {!!searchResults && (
            <React.Fragment>
              <div>Search results</div>
              <pre>{searchResults}</pre>
            </React.Fragment>
          )}
        </form>
      </div>
    </PageLoaderLayer>
  );
}
